import { Subscription } from 'rxjs'
import { filter, map, startWith, switchMap } from 'rxjs/operators'

import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core'
import { FeatureProperty } from '@classes/FeatureProperty'
import { AwsService } from '@services/aws.service'
import { FeatureService } from '@services/feature.service'
import { FileReferenceService } from '@services/file-reference.service'
import { ModelService } from '@services/model.service'
import { ProjectService } from '@services/project.service'
import { SubscriptionService } from '@services/subscription.service'

@Component({
    selector: 'shared-edit-attachment-list',
    templateUrl: './edit-attachment-list.component.html',
    styleUrls: ['./edit-attachment-list.component.css'],
    standalone: false
})
export class EditAttachmentListComponent implements OnDestroy {
    @ViewChild('fileInput') fileInput: ElementRef
    private _subscriptions: Subscription[] = []
    public selectedFeature$ = this._featureService.selectedFeature$

    get atFileLimit() { return this._subscriptionService.atFileLimit }
    get remoteAttachments() { return this.attachments.filter(attachment => attachment.fileReference == null && attachment.s3ObjectReference == null) }
    get selectedFeatureID() { return this._featureService.selectedFeatureID }
    public attachments: FeatureProperty[]
    public attachments$ = this._featureService.selectedFeature$.pipe(
        startWith(this._featureService.selectedFeature),
        filter(f => f !== undefined),
        map(feature => {
            return feature.attachments
                .sort((a, b) => {
                    const aValue: string = a.value?.toString() || ''
                    const bValue: string = b.value?.toString() || ''
                    if (aValue > bValue) return 1
                    else if (aValue < bValue) return -1
                    else return 0
                })
        })
    )
    constructor(
        private _awsService: AwsService,
        private _featureService: FeatureService,
        private _fileReferenceService: FileReferenceService,
        private _modelService: ModelService,
        private _projectService: ProjectService,
        private _subscriptionService: SubscriptionService,
    ) {
        this._subscriptions.push(
            this.attachments$.subscribe(as => this.attachments = as)
        )
    }

    ngOnDestroy() {
        this._subscriptions.forEach(subscription => subscription.unsubscribe())
    }

    downloadAttachment(attachmentProperty: FeatureProperty) {
        if (attachmentProperty.s3ObjectReferenceId) {
            this._awsService.downloadS3FileById(attachmentProperty.s3ObjectReferenceId, attachmentProperty.value).subscribe({
                error: err => console.error('Failed to download S3 attachment:', err)
            })
        } else {
            const fileReference = attachmentProperty.fileReference
            this._fileReferenceService.downloadFile(fileReference, this._projectService.currentProject.id).subscribe()
        }
    }

    removeAttachment(property: FeatureProperty) {
        this.attachments.splice(this.attachments.indexOf(property), 1)
        this.fileInput.nativeElement.value = ''
        this._featureService.deleteFeatureProperty(property).subscribe()
    }

    addLocalAttachments(files: FileList) {
        const fileArray = Array.from(files)

        this._awsService.uploadAndRegisterMultiple(fileArray, 'upload-feature-attachment', () => 'attachment').pipe(
            switchMap(s3Refs => this._featureService.addS3Attachments(this.selectedFeatureID, s3Refs.map(r => r.id)))
        ).subscribe(_ => {
            this.fileInput.nativeElement.value = ''
        })
    }

    addRemoteAttachment() {
        const value = JSON.stringify({ headers: [], url: '', displayName: '' })
        const property = new FeatureProperty('string', 'attachment', value, { featureID: this.selectedFeatureID })

        this.attachments.push(property)
        this._featureService.createFeatureProperty(property).subscribe()
    }

    getIcon(attachmentProperty: FeatureProperty) {
        const filename = attachmentProperty?.fileReference?.filename
            ?? attachmentProperty?.s3ObjectReference?.filename
            ?? attachmentProperty?.s3ObjectReference?.key?.split('/').pop()
        return this._modelService.iconClasses(filename)
    }
}